import React, { useState } from "react";
import { ChevronDown, Crown, Mic, Palette } from "lucide-react";
import { APP_STORE_URL, PLAY_STORE_URL } from "../lib/appStores";

const ACCENT = "#F5A623";

const LATEST = {
  id: "26.10.21",
  dateLabel: "October 21, 2026",
};

const SEASON_STEPS = [
  {
    step: "1",
    title: "Your finish is saved",
    body: "Your final rank is recorded on your profile as your Season 1 finish, with your leaderboard spot if you ended in the top 100.",
  },
  {
    step: "2",
    title: "Rating carries over",
    body: "Your hidden skill rating is pulled toward the middle, not wiped, so you place close to where you left off.",
  },
  {
    step: "3",
    title: "5 placements",
    body: "Season 2 opens with 5 placement pagents. The higher you finish Season 1, the higher you start.",
  },
];

function AppleLogo({ className }) {
  return (
    <svg
      viewBox="0 0 24 24"
      aria-hidden="true"
      className={className}
      fill="currentColor"
    >
      <path d="M16.365 12.89c-.025-2.64 2.16-3.91 2.258-3.968-1.232-1.8-3.148-2.047-3.824-2.072-1.626-.166-3.18.958-4.006.958-.842 0-2.126-.94-3.5-.914-1.797.027-3.46 1.046-4.386 2.653-1.876 3.255-.479 8.07 1.346 10.713.896 1.294 1.956 2.745 3.348 2.694 1.348-.054 1.856-.87 3.486-.87 1.616 0 2.088.87 3.51.84 1.454-.026 2.374-1.314 3.258-2.616.99-1.44 1.396-2.84 1.416-2.912-.031-.014-2.72-1.044-2.746-4.14zM14.17 6.26c.74-.9 1.24-2.15 1.104-3.4-1.066.043-2.36.71-3.126 1.61-.686.796-1.29 2.08-1.13 3.306 1.196.093 2.416-.608 3.152-1.516z" />
    </svg>
  );
}

function GooglePlayLogo({ className }) {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" className={className}>
      <path fill="#34A853" d="M3.06 21.28c.24.22.56.35.9.36l.14-.01 9.16-5.2-2.3-2.3-7.9 7.15z" />
      <path fill="#4285F4" d="M20.54 10.16 17.2 8.26 14.66 10.8l2.3 2.3 3.58-2.03c.7-.4.7-1.47 0-1.91z" />
      <path fill="#FBBC04" d="M3.06 2.72C2.7 3.05 2.5 3.52 2.5 4.02v15.96c0 .5.2.97.56 1.3L13.26 12 3.06 2.72z" />
      <path fill="#EA4335" d="M13.26 12 3.06 2.72c.24-.22.56-.35.9-.36l.14.01 13.1 7.45L13.26 12z" />
    </svg>
  );
}

function SectionTitle({ icon: Icon, children }) {
  return (
    <div className="flex items-center gap-3">
      <span
        className="flex h-10 w-10 items-center justify-center rounded-full"
        style={{ backgroundColor: ACCENT }}
      >
        <Icon className="h-5 w-5 text-black" />
      </span>
      <h3 className="text-2xl font-bold text-white">{children}</h3>
    </div>
  );
}

function PatchBody() {
  return (
    <div className="space-y-10 border-t border-white/10 px-6 pb-8 pt-6">
      {/* New ranks + seasons */}
      <section className="space-y-5">
        <img
          src="/images/updates/patch-26-10-21-ranks.jpg"
          alt="Two new ranks: Grand Legend and Idolized. Rankings update at midnight EST. Season 1 is planned to end January 31."
          className="w-full rounded-2xl border border-white/10"
        />

        <SectionTitle icon={Crown}>Grand Legend and Idolized</SectionTitle>

        <p className="leading-relaxed text-zinc-300">
          Legend is no longer the top. Collect 500 Legend stars and spend them
          to promote into{" "}
          <span className="font-semibold text-white">Grand Legend</span>. The
          top 500 Grand Legends by stars hold{" "}
          <span className="font-semibold text-white">Idolized</span>, a living
          cutoff that is recalculated every day. The leaderboard now updates
          once a day at midnight EST, and shows a countdown to the next update.
        </p>

        <p className="leading-relaxed text-zinc-300">
          Ranks run in seasons. The Season 1 end date is still being decided
          and will most likely be{" "}
          <span className="font-semibold text-white">January 31</span>. Here is
          what happens when it ends, and why your progress is not erased in
          Season 2.
        </p>

        <div className="grid gap-3 sm:grid-cols-3">
          {SEASON_STEPS.map((item) => (
            <div
              key={item.step}
              className="rounded-2xl border border-white/10 bg-black/40 p-5"
            >
              <p
                className="text-xs font-bold uppercase tracking-widest"
                style={{ color: ACCENT }}
              >
                Step {item.step}
              </p>
              <p className="mt-2 font-semibold text-white">{item.title}</p>
              <p className="mt-1 text-sm leading-relaxed text-zinc-300">
                {item.body}
              </p>
            </div>
          ))}
        </div>

        <ul className="space-y-2 text-sm text-zinc-300">
          <li>
            Rank badges you have earned, Hall of Fame badges, and your all-time
            personal best are never removed.
          </li>
          <li>
            Your lifetime pagent count is untouched by the reset.
          </li>
          <li>
            Your Season 1 finish stays on your profile, and shows under your
            rank while you play Season 2 placements.
          </li>
        </ul>
      </section>

      {/* Weekly themes + song carry-over */}
      <section className="space-y-5">
        <img
          src="/images/updates/patch-26-10-21-themes.jpg"
          alt="New theme every week. Songs carry over. Hit that week's voting goal to keep the look for free."
          className="w-full rounded-2xl border border-white/10"
        />

        <SectionTitle icon={Palette}>
          A new theme every week, and songs carry over
        </SectionTitle>

        <p className="leading-relaxed text-zinc-300">
          Every week now has its own theme and look, and songs carry over from
          previous pagents instead of disappearing when the week ends. That
          means longer songs are coming to Pagent, and voters won&apos;t always
          land on the opening of your performance. They may drop in during the
          middle, so a consistent performance gets a fair vote instead of the
          board tilting toward whoever had the best first few seconds before a
          voter scrolls away.
        </p>

        <div className="rounded-2xl border border-yellow-500/20 bg-yellow-500/5 p-5">
          <p
            className="text-xs font-bold uppercase tracking-widest"
            style={{ color: ACCENT }}
          >
            Keeping the look
          </p>
          <p className="mt-2 text-sm leading-relaxed text-zinc-300">
            Each theme&apos;s vote buttons and profile border are yours to keep
            if you vote on a minimum number of days that week. The goal can
            change from theme to theme, and each theme shows how many days it
            needs. Miss the goal and you can still keep it for $4.99. That is
            what keeps Pagent ad-free and free to play.
          </p>
        </div>

        <p className="text-sm leading-relaxed text-zinc-400">
          Challenges get the same treatment: longer audio and carry-over
          between weeks, so Fashion, Makeup, and Pets feel more like a runway
          than a 30-second clip.
        </p>
      </section>

      {/* Live weekend */}
      <section className="space-y-5">
        <img
          src="/images/updates/patch-26-10-21-live.jpg"
          alt="Live every weekend. Dance, karaoke, and rap. Featured live tracks may open on weekdays too."
          className="w-full rounded-2xl border border-white/10"
        />

        <SectionTitle icon={Mic}>Live every weekend</SectionTitle>

        <p className="leading-relaxed text-zinc-300">
          Dance, karaoke, and rap are live weekend battles. Pick a track while
          a live window is open and you are paired with someone who chose the
          same one for a real-time 1v1, with the community voting on the
          result.
        </p>

        <p className="leading-relaxed text-zinc-300">
          We are also looking at picking one dance and one karaoke song as live
          options during the week, so live players don&apos;t have to wait for
          the weekend.
        </p>
      </section>

      <div className="border-t border-white/10 pt-6">
        <p className="text-center text-sm font-semibold text-white">
          Get this update
        </p>
        <div className="mt-4 flex flex-wrap items-center justify-center gap-3">
          <a
            href={APP_STORE_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center gap-2 rounded-full border border-white/70 px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-white/10"
          >
            <AppleLogo className="h-4 w-4" />
            App Store
          </a>
          <a
            href={PLAY_STORE_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold text-black transition-opacity hover:opacity-90"
            style={{ backgroundColor: ACCENT }}
          >
            <GooglePlayLogo className="h-4 w-4" />
            Google Play
          </a>
        </div>
      </div>
    </div>
  );
}

// Latest app patch, collapsed until the header is clicked. `defaultOpen` is
// for the dedicated updates page, which already shows this note expanded.
export default function LatestPatchCard({ defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <article className="overflow-hidden rounded-2xl border border-white/10 bg-zinc-900/60">
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className="flex w-full items-center justify-between gap-4 px-6 py-5 text-left transition-colors hover:bg-white/5"
        aria-expanded={open}
      >
        <div>
          <p
            className="text-xs font-bold uppercase tracking-[0.2em]"
            style={{ color: ACCENT }}
          >
            Latest
          </p>
          <h2 className="mt-1 text-3xl font-black tracking-tight text-white sm:text-4xl">
            {LATEST.id}
          </h2>
          <p className="mt-1 text-sm text-white/50">{LATEST.dateLabel}</p>
          {open ? null : (
            <p className="mt-2 text-sm font-medium text-white/70">
              Tap to read what changed
            </p>
          )}
        </div>
        <ChevronDown
          className={`h-6 w-6 shrink-0 text-yellow-400 transition-transform duration-200 ${
            open ? "rotate-180" : ""
          }`}
        />
      </button>

      {open ? (
        <div className="overflow-hidden">
          <PatchBody />
        </div>
      ) : null}
    </article>
  );
}
